import React from 'react';
import '@/App.css';
import '@/index.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './lib/auth';
import { Toaster } from './components/ui/sonner';

import Landing from './pages/Landing';
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';

import CandidateDashboard from './pages/candidate/Dashboard';
import CandidateProfile from './pages/candidate/Profile';
import CandidateIntake from './pages/candidate/Intake';
import CandidateJobs from './pages/candidate/Jobs';
import CandidateApplications from './pages/candidate/Applications';
import CandidateNotifications from './pages/candidate/Notifications';
import AssessmentList from './pages/candidate/AssessmentList';
import AssessmentRun from './pages/candidate/AssessmentRun';

import EmployerDashboard from './pages/employer/Dashboard';
import EmployerJobs from './pages/employer/Jobs';
import EmployerJobDetail from './pages/employer/JobDetail';
import EmployerInbox from './pages/employer/Inbox';
import EmployerBilling from './pages/employer/Billing';
import EmployerCandidateProfile from './pages/employer/CandidateProfile';

import AdminDashboard from './pages/admin/Dashboard';
import AdminFairness from './pages/admin/Fairness';
import AdminDiscover from './pages/admin/Discover';
import AdminRoleFamilies from './pages/admin/RoleFamilies';
import AdminSkillsVocabulary from './pages/admin/SkillsVocabulary';

import Pricing from './pages/Pricing';
import PaymentSuccess from './pages/PaymentSuccess';
import Engine from './pages/Engine';
import PublicScore from './pages/PublicScore';
import Sandbox from './pages/Sandbox';
import Privacy from './pages/Privacy';
import Security from './pages/Security';
import Methodology from './pages/Methodology';
import HikariHelper from './components/hikari/HikariHelper';

const Protected = ({ role, children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10 mono text-sm">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role) return <Navigate to="/" replace />;
  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/engine" element={<Engine />} />
          <Route path="/sandbox" element={<Sandbox />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/security" element={<Security />} />
          <Route path="/methodology" element={<Methodology />} />
          <Route path="/s/:token" element={<PublicScore />} />
          <Route path="/billing/success" element={<PaymentSuccess />} />

          <Route path="/candidate" element={<Protected role="candidate"><CandidateDashboard /></Protected>} />
          <Route path="/candidate/intake" element={<Protected role="candidate"><CandidateIntake /></Protected>} />
          <Route path="/candidate/profile" element={<Protected role="candidate"><CandidateProfile /></Protected>} />
          <Route path="/candidate/jobs" element={<Protected role="candidate"><CandidateJobs /></Protected>} />
          <Route path="/candidate/assessments" element={<Protected role="candidate"><AssessmentList /></Protected>} />
          <Route path="/candidate/assessments/:id" element={<Protected role="candidate"><AssessmentRun /></Protected>} />
          <Route path="/candidate/applications" element={<Protected role="candidate"><CandidateApplications /></Protected>} />
          <Route path="/candidate/notifications" element={<Protected role="candidate"><CandidateNotifications /></Protected>} />

          <Route path="/employer" element={<Protected role="employer"><EmployerDashboard /></Protected>} />
          <Route path="/employer/jobs" element={<Protected role="employer"><EmployerJobs /></Protected>} />
          <Route path="/employer/jobs/:id" element={<Protected role="employer"><EmployerJobDetail /></Protected>} />
          <Route path="/employer/inbox" element={<Protected role="employer"><EmployerInbox /></Protected>} />
          <Route path="/employer/billing" element={<Protected role="employer"><EmployerBilling /></Protected>} />
          <Route path="/employer/candidate/:id" element={<Protected role="employer"><EmployerCandidateProfile /></Protected>} />

          <Route path="/admin" element={<Protected role="admin"><AdminDashboard /></Protected>} />
          <Route path="/admin/fairness" element={<Protected role="admin"><AdminFairness /></Protected>} />
          <Route path="/admin/discover" element={<Protected role="admin"><AdminDiscover /></Protected>} />
          <Route path="/admin/role-families" element={<Protected role="admin"><AdminRoleFamilies /></Protected>} />
          <Route path="/admin/skills-vocabulary" element={<Protected role="admin"><AdminSkillsVocabulary /></Protected>} />
        </Routes>
        <HikariHelper />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
